import { AROM_ASSESSMENT } from "../models/Enums";
import {
  AromMenuOptions,
  ASSESSMENT_STATUSES,
  DL_MODELS,
} from "../movenet/params";
import * as Sentry from "@sentry/browser";
import { UpperBodyAromAssessmentStates } from "./assessment_states";
import { UpperBodyAromAudioPlayer } from "./audio-player";
import { config } from "./env_config";
import { MultiPoseUtility } from "./multipose_utility";
import { MoveNetPoseWrapper } from "./pose-wrapper";
import { PatientSelection } from "./patient_selection";
import {
  recordAdductionAbduction,
  recordLeftElbowFlexionExtension,
  recordLeftInternalExternalShoulderRotation,
  recordLeftShoulderFlexExt,
  recordRightElbowFlexionExtension,
  recordRightInternalExternalShoulderRotation,
  recordRightShoulderFlexExt,
} from "./record_AROM_angles";
import { SitStandProcessing } from "./sitStandProcessing";
import { GestureDetector } from "./start_gesture_detector";
import {
  startiOSVoiceListening,
  stopiOSVoiceListening,
} from "../utils/mobile-bridge";

const time_per_assessment_in_sec = 16;

export const calculation_metric = {
  adduction_abduction: {
    left_adduction: null,
    left_abduction: null,
    right_adduction: null,
    right_abduction: null,
  },
  shoulder_flexion_extension: {
    left_flexion: null,
    left_extension: null,
    right_flexion: null,
    right_extension: null,
  },
  elbow_flexion_extension: {
    left_flexion: null,
    left_extension: null,
    right_flexion: null,
    right_extension: null,
  },
  internal_external_shoulder_rotation: {
    left_external: null,
    left_internal: null,
    right_external: null,
    right_internal: null,
  },
};

export const adduction_abduction_right = [];
export const adduction_abduction_left = [];
export const shoulder_flexion_extension_right = [];
export const shoulder_flexion_extension_left = [];
export const elbow_flexion_extension_right = [];
export const elbow_flexion_extension_left = [];
export const internal_external_shoulder_rotation_right = [];
export const internal_external_shoulder_rotation_left = [];

export class UpperBodyArom {
  /**
   * Initializes the Upper Body AROM assessment with necessary callbacks,
   * UI elements, and default state values.
   * @param {Object} camera - Camera interface for video input and drawing.
   * @param {Function} switch_to_single_pose - Callback to switch to single-pose model.
   * @param {Function} reset_pose_estimator - Callback to reset pose estimator.
   * @param {Function} switch_to_multi_pose - Callback to switch back to multi-pose model.
   */
  constructor(
    camera,
    switch_to_single_pose,
    reset_pose_estimator,
    switch_to_multi_pose
  ) {
    this.switch_to_single_pose_callback = switch_to_single_pose;
    this.switch_to_multi_pose_callback = switch_to_multi_pose;
    this.reset_pose_estimator = reset_pose_estimator;
    this.camera = camera;
    this.current_state = UpperBodyAromAssessmentStates.begin_assessment;
    this.gesture_detector = new GestureDetector();
    this.sit_stand_processing = new SitStandProcessing();
    this.end_time = 0;
    this.first_frame = true;
    this.audio_player = new UpperBodyAromAudioPlayer();
    this.therapist_id = -1;
    this.patient_id = -1;
    this.patient_ID_changed = false;
    this.track_patient = false;
    this.start_time = 0;
    this.is_calibrated = false;
    this.calibration_error_occured = false;
    this.initialize_calibration_error_occured = true;
    this.feet_box_x_min = 0;
    this.feet_box_x_max = 0;
    this.feet_box_y_min = 0;
    this.feet_box_y_max = 0;
    this.frames_with_no_patient = 0;
    this.frames_with_patient_outside_extended_box = 0;
    this.insetoutputCanvas = document.getElementById("insetoutput");
    this.are_you_there_flag = true;
    this.are_you_there_timer = 0;
    this.current_time = 0;
    this.first_frame_calibration_2 = true;
    this.time_calibration_2 = 0;
    this.inactive_for_60_seconds = false;
    this.start_timer_reference = 0;
    this.within_bound_start_flag = false;
    this.assessment_timer = 0;
    this.first_frame_time = 0;
    this.angle_value = -1;
    this.patient_within_box_check = false;
    this.patient_within_box_timer = 0;
    this.current_arom_sub_assessment = AROM_ASSESSMENT.Shoulder_AAA;
    this.arom_left_high_text = document.getElementById("arom-left-high-text");
    this.arom_left_high_value = document.getElementById("arom-left-high-value");
    this.arom_left_low_text = document.getElementById("arom-left-low-text");
    this.arom_left_low_value = document.getElementById("arom-left-low-value");
    this.arom_right_high_text = document.getElementById("arom-right-high-text");
    this.arom_right_high_value = document.getElementById(
      "arom-right-high-value"
    );
    this.arom_right_low_text = document.getElementById("arom-right-low-text");
    this.arom_right_low_value = document.getElementById("arom-right-low-value");
    this.arom_realtime_angle_left = document.getElementById(
      "arom-realtime-angle-left"
    );
    this.arom_realtime_angle_right = document.getElementById(
      "arom-realtime-angle-right"
    );
    this.arom_assessment_counter_left = document.getElementById(
      "arom-realtime-angle-left-value"
    );
    this.arom_assessment_counter_right = document.getElementById(
      "arom-realtime-angle-right-value"
    );
    let videoWidth = 1080;
    let videoHeight = 1920;
    this.options = {
      sx: 0,
      sy: 0,
      sw: videoWidth,
      sh: videoHeight,
    };
    this.arom_retry = document.getElementById("arom-retry");
    this.display_modalFaceCamera = false;
    this.play_beep_sound = true;
    this.handFlag = true;
    this.shown_persistent_icon = false;
    this.frames_out_of_box = 0;
    this.assessment_function = null;
    if (this.insetoutputCanvas) {
      this.insetoutputCanvas.style.display = "none";
    }
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let userID = localStorage.getItem("UserID")
      ? localStorage.getItem("UserID")
      : "000";
    let businessCode = localStorage.getItem("BusinessCode")
      ? localStorage.getItem("BusinessCode")
      : "000";

    Sentry.setTag("UserID", userID);
    Sentry.setTag("Patient Server ID", patientServerID);
    Sentry.setTag("Business Code", businessCode);

    this.sentry_prefix = `UB_AROM_` + new Date().toISOString();
    ASSESSMENT_STATUSES.sentry_message = this.sentry_prefix;
    ASSESSMENT_STATUSES.are_you_there = false;
    ASSESSMENT_STATUSES.incomplete_test = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;
    ASSESSMENT_STATUSES.assessment_error = false;
    this.sentry_start_time = 0;
    this.are_you_there_modal_timer =
      config["are-you-there-timer-in-seconds"] * 1000;
    this.enabled_voice_listener = false;
    this.voice_command_received = false;

    // Remote NEXT: movements skipped (reported as skipped_phases) and movements measured to the end
    this.skipped_phases = [];
    this.completed_phase_count = 0;
    // Remote REWIND: the phase that most recently completed or was skipped, while it can still be
    // redone (until the next phase's start is accepted); null otherwise
    this.rewind_target = null;

    // Store the event listener reference for cleanup
    this.voiceCommandListener = (event) => {
      const command = event.detail.command;
      console.log("🎤 [UpperBodyAROM] Received voice command:", command);

      // Example: Trigger something based on the command
      if (command === "START") {
        console.log("Voice Triggered START command");
        this.voice_command_received = true;
        this.enabled_voice_listener = false;
        stopiOSVoiceListening();
      }
    };

    window.addEventListener("voice-command", this.voiceCommandListener);

    // Expose instance on window for retry handler in assessment-page.tsx
    window.aromInstance = this;

    // Remote UP/DOWN patient selection: the participant the therapist selects before each movement.
    // The selection is kept for the later movements.
    this.patient_selection = new PatientSelection();
  }

  /**
   * Cleanup voice listeners and reset states
   */
  cleanupVoiceListeners() {
    // Stop voice listening on any termination condition
    if (this.enabled_voice_listener) {
      console.log("🔊 Stopping voice listening due to assessment termination");
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
  }

  /**
   * Complete cleanup including event listener removal
   */
  destroy() {
    this.cleanupVoiceListeners();
    if (this.voiceCommandListener) {
      window.removeEventListener("voice-command", this.voiceCommandListener);
      this.voiceCommandListener = null;
    }
  }

  /**
   * Checks if patient ankles are within the bounding box.
   * Uses extended_patient_box for left/right sub-assessments (ankles overlap when turned sideways).
   * Draws green box when in, brown box when out.
   * Returns true if error was triggered (patient out of box for 30+ frames).
   */
  checkAndHandleOutOfBox(poses) {
    if (!poses || poses.length === 0) {
      this.sit_stand_processing.draw_patient_box();
      this.frames_out_of_box++;
      if (this.frames_out_of_box >= 30) {
        this.triggerOutOfBoxError();
        return true;
      }
      return false;
    }

    const pose = poses[0];
    if (!pose || !pose.keypoints) {
      this.sit_stand_processing.draw_patient_box();
      this.frames_out_of_box++;
      if (this.frames_out_of_box >= 30) {
        this.triggerOutOfBoxError();
        return true;
      }
      return false;
    }

    // Pick correct ankle index based on model (BlazePose: 27/28, MoveNet: 15/16)
    const isSinglePose = pose.keypoints.length > 20;
    const leftAnkleIdx = isSinglePose ? 27 : 15;
    const rightAnkleIdx = isSinglePose ? 28 : 16;

    const leftAnkle = this.camera.scale_to_fit({
      x: pose.keypoints[leftAnkleIdx].x,
      y: pose.keypoints[leftAnkleIdx].y,
    });
    const rightAnkle = this.camera.scale_to_fit({
      x: pose.keypoints[rightAnkleIdx].x,
      y: pose.keypoints[rightAnkleIdx].y,
    });

    // Use extended box for left/right sub-assessments (more buffer for sideways ankles)
    const box = this.current_arom_sub_assessment === AROM_ASSESSMENT.Shoulder_AAA
      ? this.sit_stand_processing.patient_box
      : this.sit_stand_processing.extended_patient_box;

    const inBox =
      leftAnkle.x > box.x &&
      leftAnkle.x < box.x + box.width &&
      leftAnkle.y > box.y &&
      leftAnkle.y < box.y + box.height &&
      rightAnkle.x > box.x &&
      rightAnkle.x < box.x + box.width &&
      rightAnkle.y > box.y &&
      rightAnkle.y < box.y + box.height;

    if (inBox) {
      this.sit_stand_processing.draw_patient_box_green();
      this.frames_out_of_box = 0;
      return false;
    }

    this.sit_stand_processing.draw_patient_box();
    this.frames_out_of_box++;
    if (this.frames_out_of_box >= 30) {
      this.triggerOutOfBoxError();
      return true;
    }
    return false;
  }

  /**
   * Triggers out-of-box error: kills all audio/UI, shows error modal.
   * Sets error flag FIRST to prevent race conditions.
   */
  triggerOutOfBoxError() {
    // P0: Set error flag FIRST before any UI updates
    ASSESSMENT_STATUSES.assessment_error = true;
    ASSESSMENT_STATUSES.assessment_Completed = true;

    this.current_state = UpperBodyAromAssessmentStates.test_complete;

    // Stop voice listening
    this.cleanupVoiceListeners();

    // Stop any playing audio
    this.audio_player.stop();

    // Hide all UI elements — error modal takes priority
    document.getElementById("hdnHideHandRaisePersistentIcon")?.click();

    // Show error modal
    document.getElementById("hdnAssessementErrorPopup")?.click();

    // Play assessment interrupted audio
    this.audio_player.play(UpperBodyAromAssessmentStates.assessment_interrupted);

    localStorage.removeItem("modalDisplayed");
    this.frames_out_of_box = 0;

    this.append_sentry_message(
      "Assessment ERROR - Patient moved out of bounding box during " +
        this.current_arom_sub_assessment
    );
  }

  /**
   * Retries the current sub-assessment from its instructions (calibration_2).
   * Preserves completed sub-assessments' metrics, resets only current sub-assessment data.
   */
  retryCurrentPhase() {
    this.append_sentry_message(
      "Retrying sub-assessment: " + this.current_arom_sub_assessment
    );

    // Reset to calibration_2 (where instructions/GIF plays for current sub-assessment)
    this.current_state =
      UpperBodyAromAssessmentStates.abduction_adduction_calibration_2;
    this.assessment_timer = this.current_time;

    // Reset flags
    this.frames_out_of_box = 0;
    this.frames_with_no_patient = 0;
    this.shown_persistent_icon = false;
    this.voice_command_received = false;
    this.enabled_voice_listener = false;
    this.first_frame_calibration_2 = true;
    this.play_beep_sound = true;
    this.handFlag = true;

    // Reset gesture detector
    this.gesture_detector.reset();

    // Switch back to multi-pose for hand raise detection
    this.reset_pose_estimator();
    this.switch_to_multi_pose_callback();

    // Hide real-time angle UI
    this.arom_realtime_angle_left.style.display = "none";
    this.arom_realtime_angle_right.style.display = "none";

    // Reset only current sub-assessment's metrics
    switch (this.current_arom_sub_assessment) {
      case AROM_ASSESSMENT.Shoulder_AAA:
        calculation_metric.adduction_abduction.left_abduction = null;
        calculation_metric.adduction_abduction.left_adduction = null;
        calculation_metric.adduction_abduction.right_abduction = null;
        calculation_metric.adduction_abduction.right_adduction = null;
        break;
      case AROM_ASSESSMENT.Right_Shoulder_FAE:
        calculation_metric.shoulder_flexion_extension.left_flexion = null;
        calculation_metric.shoulder_flexion_extension.left_extension = null;
        break;
      case AROM_ASSESSMENT.Left_Shoulder_FAE:
        calculation_metric.shoulder_flexion_extension.right_flexion = null;
        calculation_metric.shoulder_flexion_extension.right_extension = null;
        break;
      case AROM_ASSESSMENT.Right_Elbow_FAE:
        calculation_metric.elbow_flexion_extension.left_flexion = null;
        calculation_metric.elbow_flexion_extension.left_extension = null;
        break;
      case AROM_ASSESSMENT.Left_Elbow_FAE:
        calculation_metric.elbow_flexion_extension.right_flexion = null;
        calculation_metric.elbow_flexion_extension.right_extension = null;
        break;
      case AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation:
        calculation_metric.internal_external_shoulder_rotation.left_internal = null;
        calculation_metric.internal_external_shoulder_rotation.left_external = null;
        break;
      case AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation:
        calculation_metric.internal_external_shoulder_rotation.right_internal = null;
        calculation_metric.internal_external_shoulder_rotation.right_external = null;
        break;
      default:
        break;
    }

    // Clear error flags
    ASSESSMENT_STATUSES.assessment_error = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;

    this.append_sentry_message(
      "Retry - returning to calibration_2 for " + this.current_arom_sub_assessment
    );
  }

  /**
   * Main update loop called on each video frame. Manages state transitions,
   * UI interactions, and calls to specific assessment logic.
   * @param {Array} poses - Array of detected poses from MoveNet.
   */
  update(poses) {
    this.current_time = Date.now();

    // The participant the therapist selected with the remote, while it can still be changed.
    // patient_id also makes movenet draw this person's skeleton green, and detect_patient prefers
    // this person (by id). Without a selection patient_id is left as it was.
    if (this.is_patient_selection_open()) {
      const patient = this.patient_selection.update(poses);
      if (patient) this.patient_id = patient.id;
    }

    // Handle restart, skip, and resume features
    this.restart_sub_assessment_feature();
    this.restart_phase_feature();
    this.skip_phase_feature();

    // --------------------------- TERMINATION CONDITIONS --------------------------------
    if (
      this.current_state === UpperBodyAromAssessmentStates.test_complete ||
      ASSESSMENT_STATUSES.calibration_error_state === true ||
      this.current_state ===
        UpperBodyAromAssessmentStates.assessment_interrupted ||
      ASSESSMENT_STATUSES.exit_assessment_modal === true
    ) {
      // Stop voice listening on any termination condition
      this.cleanupVoiceListeners();
      if (ASSESSMENT_STATUSES.calibration_error_state) {
        this.calibration_error_occured = true;
      }
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
      return;
    }

    // Handle inactivity prompt ("Are you there?")
    if (!poses || poses.length === 0) {
      if (this.are_you_there_flag) {
        this.are_you_there_flag = false;
        this.are_you_there_timer = Date.now();
      }
      const elapsed = this.current_time - this.are_you_there_timer;
      if (elapsed >= 60000 && !this.inactive_for_60_seconds) {
        this.inactive_for_60_seconds = true;
        this.append_sentry_message(
          "No pose/activity detected for more than 60 seconds."
        );
      }
      if (elapsed >= this.are_you_there_modal_timer) {
        this.are_you_there_flag = true;
        this.are_you_there_timer = 0;
        document.getElementById("hdnAreYouTherePopup").click();
      }
    } else {
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
    }

    if (document.readyState !== "complete") return;

    if (this.first_frame) {
      // --------------------------- INITIAL CALIBRATION --------------------------------
      calculation_metric.adduction_abduction.left_adduction = null;
      calculation_metric.adduction_abduction.left_abduction = null;
      calculation_metric.adduction_abduction.right_adduction = null;
      calculation_metric.adduction_abduction.right_abduction = null;
      calculation_metric.shoulder_flexion_extension.left_extension = null;
      calculation_metric.shoulder_flexion_extension.left_flexion = null;
      calculation_metric.shoulder_flexion_extension.right_extension = null;
      calculation_metric.shoulder_flexion_extension.right_flexion = null;
      calculation_metric.elbow_flexion_extension.left_extension = null;
      calculation_metric.elbow_flexion_extension.left_flexion = null;
      calculation_metric.elbow_flexion_extension.right_extension = null;
      calculation_metric.elbow_flexion_extension.right_flexion = null;
      calculation_metric.internal_external_shoulder_rotation.left_external =
        null;
      calculation_metric.internal_external_shoulder_rotation.left_internal =
        null;
      calculation_metric.internal_external_shoulder_rotation.right_external =
        null;
      calculation_metric.internal_external_shoulder_rotation.right_internal =
        null;

      sessionStorage.setItem("phaseNo", "1");
      sessionStorage.setItem(
        "aromAssessmentIndex",
        AROM_ASSESSMENT.Shoulder_AAA
      );
      this.current_arom_sub_assessment = AROM_ASSESSMENT.Shoulder_AAA;
      this.assessment_function = (poses) =>
        recordAdductionAbduction(this, poses);
      this.first_frame = false;
      this.audio_player.play(
        UpperBodyAromAssessmentStates.begin_assessment,
        this.current_arom_sub_assessment
      );
      document.getElementById("hdnAssessmentStartUpMessage").click();
      this.camera.flip_horizontal();

      // Log initial DL model load status and screen resolution
      if (DL_MODELS.MessageForSentry === "") {
        this.append_sentry_message("DL_Model already loaded");
      } else {
        this.append_sentry_message(DL_MODELS.MessageForSentry);
        DL_MODELS.MessageForSentry = "";
      }
      this.append_sentry_message(
        "Screen Resolution: " +
          this.sit_stand_processing.canvas.width +
          "x" +
          this.sit_stand_processing.canvas.height
      );
      this.append_sentry_message(
        "'Place chair around 8 feet away from phone' modal is displayed"
      );
      this.first_frame_time = this.current_time;
    }

    if (this.track_patient) {
      // this.assessment_error_check(poses);
    }

    try {
      switch (this.current_state) {
        // --------------------------- 1) BEGIN ASSESSMENT --------------------------------
        case UpperBodyAromAssessmentStates.begin_assessment:
          this.sit_stand_processing.draw_patient_box();
          if (
            // With a remote selection, only the selected person counts; otherwise anyone in the box
            this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            this.gesture_detector._screen_color_change();
            if (!this.patient_within_box_check) {
              this.patient_within_box_check = true;
              this.patient_within_box_timer = this.current_time;
            }
            if (this.current_time - this.patient_within_box_timer >= 3000) {
              this.within_bound_start_flag = true;
            }
          } else {
            this.within_bound_start_flag = false;
            this.patient_within_box_check = false;
            this.patient_within_box_timer = this.current_time;
            return;
          }
          if (
            !this.audio_player.is_available() ||
            this.current_time - this.first_frame_time <= 7000
          ) {
            return;
          }
          if (this.within_bound_start_flag) {
            document.getElementById("hdnParticiantWithinBox").click();
            this.append_sentry_message(
              "Participant is within the Bounding box"
            );
            this.within_bound_start_flag = false;
            this.assessment_timer = this.current_time;
            this.current_state = UpperBodyAromAssessmentStates.calibration_1;
            return;
          }
          break;

        // --------------------------- 2) RAISE THE HAND --------------------------------
        // case UpperBodyAromAssessmentStates.raise_the_hand:
        //   if (
        //     this.are_within_bbox(poses, this.sit_stand_processing.patient_box)
        //   ) {
        //     if (this.current_time - this.assessment_timer > 3000) {
        //       document.getElementById("hdnSitStandRaiseHandMessage").click();
        //       this.current_state = UpperBodyAromAssessmentStates.with_in_bounds;
        //       this.assessment_timer = this.current_time;
        //     }
        //   } else {
        //     this.sit_stand_processing.draw_patient_box();
        //   }
        //   break;

        // --------------------------- 3) WITHIN BOUNDS --------------------------------
        case UpperBodyAromAssessmentStates.with_in_bounds:
          if (
            this.are_within_bbox(poses, this.sit_stand_processing.patient_box)
          ) {
            // Detect hand raise every frame (not gated by timer)
            this.gesture_detector.detect_start_gesture(poses);

            // Voice listener gated by is_available() to avoid killing audio on iOS
            if (
              !this.enabled_voice_listener &&
              !this.voice_command_received &&
              this.audio_player.is_available()
            ) {
              console.log("🔊 Start of voice listening...");
              setTimeout(() => {
                startiOSVoiceListening();
              }, 100);
              this.enabled_voice_listener = true;
            }

            // Show persistent icon once audio finishes (algorithm-driven)
            if (!this.shown_persistent_icon && this.audio_player.is_available()) {
              this.shown_persistent_icon = true;
              document.getElementById("hdnRaiseArmStopModalPopup")?.click();
            }

            if (
              this.gesture_detector.start_gesture_issued() ||
              this.voice_command_received
            ) {
              // Stop audio immediately if still playing
              if (!this.audio_player.is_available()) this.audio_player.stop();

              if (this.enabled_voice_listener) {
                console.log("🔊 Stopping voice listening...");
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }

              document.getElementById("hdnHideHandRaisePersistentIcon")?.click();

              this.assessment_timer = this.current_time;
              this.current_state =
                UpperBodyAromAssessmentStates.calibration_1;
              this.start_time = this.current_time;
              this.therapist_id = this.gesture_detector.gesture_issued_by;
              this.gesture_detector.reset();
              document.getElementById("hdnCalibrationMessage").click();
              this.audio_player.play(
                UpperBodyAromAssessmentStates.calibrated,
                this.current_arom_sub_assessment
              );
              this.append_sentry_message("Calibration Completed.");
              this.append_sentry_message(
                "Number of persons in the frame while gesture detection: " +
                  poses.length
              );
              this.assessment_timer = this.current_time;
              this.voice_command_received = false;
            }
          } else {
            this.sit_stand_processing.draw_patient_box();
          }
          break;

        // --------------------------- 4) CALIBRATION PHASE 1 --------------------------------
        case UpperBodyAromAssessmentStates.calibration_1:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 3000 &&
            !this.display_modalFaceCamera
          ) {
            this.display_modalFaceCamera = true;
            document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
            this.audio_player.play(
              UpperBodyAromAssessmentStates.calibration_1,
              this.current_arom_sub_assessment
            );
            this.assessment_timer = this.current_time;
          } else if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 1000 &&
            this.display_modalFaceCamera
          ) {
            this.display_modalFaceCamera = false;
            document
              .getElementById("hdnFaceCameraPalmForwardPhase1close")
              .click();
            this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
            this.assessment_timer = this.current_time;
            if (this.detect_patient(poses)) {
              this.track_patient = true;
            }
          }
          break;

        // --------------------------- 5) HAND ORIENTATION --------------------------------
        case UpperBodyAromAssessmentStates.hand_orientation:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 1000
          ) {
            document
              .getElementById("hdnAromAssessmentHandOrientationMessage")
              .click();
            this.audio_player.play(
              UpperBodyAromAssessmentStates.hand_orientation,
              this.current_arom_sub_assessment
            );
            this.current_state =
              UpperBodyAromAssessmentStates.abduction_adduction_calibration_2;
            this.assessment_timer = this.current_time;
          }
          break;

        // --------------------------- 6) CALIBRATION PHASE 2 --------------------------------
        case UpperBodyAromAssessmentStates.abduction_adduction_calibration_2:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 2000
          ) {
            if (this.first_frame_calibration_2) {
              document.getElementById("hdnAromAssessmentGifMessage").click();
              this.first_frame_calibration_2 = false;
              this.time_calibration_2 = this.current_time;
            }
            if (
              this.current_arom_sub_assessment ===
                AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation ||
              this.current_arom_sub_assessment ===
                AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation
            ) {
              if (this.current_time - this.time_calibration_2 > 2000) {
                this.first_frame_calibration_2 = true;
                this.audio_player.play(
                  UpperBodyAromAssessmentStates.abduction_adduction_calibration_2,
                  this.current_arom_sub_assessment
                );
                this.current_state =
                  UpperBodyAromAssessmentStates.abduction_adduction_calibration_3;
                this.assessment_timer = this.current_time;
              }
            } else {
              this.first_frame_calibration_2 = true;
              this.audio_player.play(
                UpperBodyAromAssessmentStates.abduction_adduction_calibration_2,
                this.current_arom_sub_assessment
              );
              this.current_state =
                UpperBodyAromAssessmentStates.abduction_adduction_calibration_3;
              this.assessment_timer = this.current_time;
            }
          }
          break;

        // --------------------------- 7) CALIBRATION PHASE 3 --------------------------------
        case UpperBodyAromAssessmentStates.abduction_adduction_calibration_3:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 3000
          ) {
            document.getElementById("hdnAromTherapistReadyMessage")?.click();
            this.shown_persistent_icon = false;
            this.audio_player.play(
              UpperBodyAromAssessmentStates.abduction_adduction_calibration_3,
              this.current_arom_sub_assessment
            );
            this.current_state =
              UpperBodyAromAssessmentStates.abduction_adduction_calibration_4;
            this.assessment_timer = this.current_time;
          }
          break;

        // --------------------------- 8) CALIBRATION PHASE 4 --------------------------------
        case UpperBodyAromAssessmentStates.abduction_adduction_calibration_4:
          if (this.shown_persistent_icon) {
            // With a remote selection, the selected person; otherwise whoever MoveNet lists first
            if (this.checkAndHandleOutOfBox(this.patient_selection.start_check_poses(poses))) break;
          } else {
            this.sit_stand_processing.draw_patient_box();
          }
          // Show persistent icon once audio finishes (not gated by bounding box —
          // patient can't see the box while full modal is covering the screen)
          if (!this.shown_persistent_icon && this.audio_player.is_available()) {
            this.shown_persistent_icon = true;
            document.getElementById("hdnRaiseArmStopModalPopup")?.click();
          }

          if (
            // With a remote selection, only the selected person counts; otherwise anyone in the box
            this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            // Detect hand raise every frame (not gated by audio or handFlag)
            this.gesture_detector.detect_start_gesture(poses);

            // Voice listener gated by is_available() to avoid killing audio on iOS
            if (
              !this.enabled_voice_listener &&
              !this.voice_command_received &&
              this.audio_player.is_available()
            ) {
              console.log("🔊 Start of voice listening...");
              setTimeout(() => {
                startiOSVoiceListening();
              }, 100);
              this.enabled_voice_listener = true;
            }

            if (
              this.gesture_detector.start_gesture_issued() ||
              this.voice_command_received
            ) {
              this.handFlag = false;
              this.rewind_target = null; // started: REWIND can no longer go back
              this.shown_persistent_icon = true;

              document.getElementById("hdnHideHandRaisePersistentIcon")?.click();

              if (!this.audio_player.is_available()) this.audio_player.stop();
              if (this.enabled_voice_listener) {
                console.log("🔊 Stopping voice listening...");
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }

              this.reset_pose_estimator();
              this.switch_to_blazepose(poses);
              this.therapist_id = this.gesture_detector.gesture_issued_by;
              this.gesture_detector.reset();
              this.append_sentry_message("Calibration Completed.");
              this.append_sentry_message(
                "Number of persons in the frame while gesture detection: " +
                  poses.length
              );
              document.getElementById("hdnAromPhase1StartMessage").click();
              this.audio_player.play(
                UpperBodyAromAssessmentStates.abduction_adduction_calibration_4,
                this.current_arom_sub_assessment
              );
              this.voice_command_received = false;
              setTimeout(() => {
                // Not if the movement was restarted meanwhile (remote stop + REWIND resets handFlag)
                if (this.handFlag) return;
                this.current_state =
                  UpperBodyAromAssessmentStates.pre_assessment;
              }, 5000);
              this.assessment_timer = this.current_time;
              setTimeout(() => {
                if (
                  [
                    AROM_ASSESSMENT.Shoulder_AAA,
                    AROM_ASSESSMENT.Left_Shoulder_FAE,
                    AROM_ASSESSMENT.Left_Elbow_FAE,
                    AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation,
                  ].includes(this.current_arom_sub_assessment)
                ) {
                  this.arom_realtime_angle_left.style.display = "flex";
                }
                if (
                  [
                    AROM_ASSESSMENT.Shoulder_AAA,
                    AROM_ASSESSMENT.Right_Shoulder_FAE,
                    AROM_ASSESSMENT.Right_Elbow_FAE,
                    AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation,
                  ].includes(this.current_arom_sub_assessment)
                ) {
                  this.arom_realtime_angle_right.style.display = "flex";
                }
              }, 1000);
            }
          } else {
            if (this.enabled_voice_listener) {
              console.log("🔊 Stopping voice listening...");
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
          }
          break;

        // --------------------------- 9) PRE-ASSESSMENT --------------------------------
        case UpperBodyAromAssessmentStates.pre_assessment:
          if (this.checkAndHandleOutOfBox(poses)) break;
          this.handFlag = true;
          if (this.initialize_calibration_error_occured) {
            this.calibration_error_occured = false;
            this.initialize_calibration_error_occured = false;
          }
          if (poses.length > 1) break;
          if (this.audio_player.is_available()) {
            this.assessment_timer = this.current_time;
            AromMenuOptions.actualAssessmentStartTime = this.current_time;
            this.current_state =
              UpperBodyAromAssessmentStates.abduction_adduction_assessment;
            document.getElementById("hdnAromPhase1StartMessageClose").click();
            document.getElementById("btn-arom-assessment-counter").click();
          }
          this.assessment_function(poses);

          break;

        // --------------------------- 10) ASSESSMENT IN PROGRESS --------------------------------
        case UpperBodyAromAssessmentStates.abduction_adduction_assessment:
          if (this.checkAndHandleOutOfBox(poses)) break;
          this.initialize_calibration_error_occured = true;
          this.assessment_function(poses);
          let time_left =
            time_per_assessment_in_sec -
            Math.floor((this.current_time - this.assessment_timer) / 1000);
          if (time_left <= 3 && this.play_beep_sound) {
            this.play_beep_sound = false;
            this.audio_player.play(
              UpperBodyAromAssessmentStates.abduction_adduction_assessment,
              this.current_arom_sub_assessment
            );
          }
          if (
            this.current_time - this.assessment_timer >=
            time_per_assessment_in_sec * 1000
          ) {
            this.assessment_timer = this.current_time;
            this.completed_phase_count++;
            this.rewind_target = this.current_arom_sub_assessment;
            this.current_state =
              UpperBodyAromAssessmentStates.post_abduction_adduction_modal;
            document.getElementById("hdnAromCompletionMessage").click();
            document.getElementById("btn-arom-assessment-counter").click();
            this.audio_player.play(
              UpperBodyAromAssessmentStates.post_abduction_adduction_modal,
              this.current_arom_sub_assessment
            );
          }
          document.getElementById("angle_values").innerText =
            `Adduction: (L:${Math.round(
              calculation_metric.adduction_abduction.left_adduction
            )}, R:${Math.round(
              calculation_metric.adduction_abduction.right_adduction
            )}), ` +
            `Abduction: (L:${Math.round(
              calculation_metric.adduction_abduction.left_abduction
            )}, R:${Math.round(
              calculation_metric.adduction_abduction.right_abduction
            )})`;
          break;

        // --------------------------- 11) POST-ASSESSMENT --------------------------------
        case UpperBodyAromAssessmentStates.post_abduction_adduction_modal:
          this.sit_stand_processing.draw_patient_box_green();
          this.arom_realtime_angle_left.style.display = "none";
          this.arom_realtime_angle_right.style.display = "none";
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer >= 3000
          ) {
            this.assessment_timer = this.current_time;
            this.play_beep_sound = true;
            this.switch_to_next_sub_assessment_state();
          }
          break;

        // --------------------------- 12) ALL SUB-ASSESSMENTS COMPLETE --------------------------------
        case UpperBodyAromAssessmentStates.all_sub_assessments_complete:
          this.end_time = this.current_time;
          let response = this.build_response();
          this.append_sentry_message(
            "Assessment response:" + JSON.stringify(response)
          );
          if (window["AssessmentPageReference"]) {
            window["AssessmentPageReference"].CompleteAssessment(response);
          } else {
            console.log("window['AssessmentPageReference'] not found");
          }
          // Stop voice listening on successful completion
          this.cleanupVoiceListeners();
          this.current_state = UpperBodyAromAssessmentStates.test_complete;
          ASSESSMENT_STATUSES.assessment_Completed = true;
          break;

        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Checks if any detected poses have feet within the given bounding box.
   * @param {Array} poses - Array of detected poses.
   * @param {Object} bounding_box - Bounding box with x, y, width, height.
   * @returns {boolean} - True if at least one pose is within the box.
   */
  are_within_bbox(poses, bounding_box) {
    let result = MultiPoseUtility.are_both_ankles_in_canvas_box(
      poses,
      bounding_box,
      this.camera,
      0.2
    );
    this.sit_stand_processing.is_patient_in_box_status = result;
    return result;
  }

  /**
   * Builds the final assessment response object with summary metrics.
   * @returns {Object} - Assessment result including start/end times and angles.
   */
  build_response() {
    return {
      test_type: "upper_body_arom",
      start_time: this.start_time.toString(),
      end_time: this.end_time.toString(),
      // Movements skipped with the remote's NEXT (AROM_ASSESSMENT values); their angles are 0
      skipped_phases: this.skipped_phases,
      Abduction_Adduction: {
        AdductionLeft: Math.round(
          calculation_metric.adduction_abduction.left_adduction
        ),
        AbductionLeft: Math.round(
          calculation_metric.adduction_abduction.left_abduction
        ),
        AdductionRight: Math.round(
          calculation_metric.adduction_abduction.right_adduction
        ),
        AbductionRight: Math.round(
          calculation_metric.adduction_abduction.right_abduction
        ),
      },
      Shoulder_Flexion_Extension: {
        ShoulderExtensionLeft: Math.round(
          calculation_metric.shoulder_flexion_extension.left_extension
        ),
        ShoulderFlexionLeft: Math.round(
          calculation_metric.shoulder_flexion_extension.left_flexion
        ),
        ShoulderExtensionRight: Math.round(
          calculation_metric.shoulder_flexion_extension.right_extension
        ),
        ShoulderFlexionRight: Math.round(
          calculation_metric.shoulder_flexion_extension.right_flexion
        ),
      },
      Elbow_Flexion_Extension: {
        ElbowFlexionLeft: Math.round(
          calculation_metric.elbow_flexion_extension.left_flexion
        ),
        ElbowExtensionLeft: Math.round(
          calculation_metric.elbow_flexion_extension.left_extension
        ),
        ElbowFlexionRight: Math.round(
          calculation_metric.elbow_flexion_extension.right_flexion
        ),
        ElbowExtensionRight: Math.round(
          calculation_metric.elbow_flexion_extension.right_extension
        ),
      },
      Shoulder_Internal_External_Rotation: {
        ExternalLeft: Math.round(
          calculation_metric.internal_external_shoulder_rotation.left_external
        ),
        InternalLeft: Math.round(
          calculation_metric.internal_external_shoulder_rotation.left_internal
        ),
        ExternalRight: Math.round(
          calculation_metric.internal_external_shoulder_rotation.right_external
        ),
        InternalRight: Math.round(
          calculation_metric.internal_external_shoulder_rotation.right_internal
        ),
      },
    };
  }

  /**
   * Identifies the patient pose from multi-person detections.
   * Prefers already-tracked ID or chooses closest feet within patient box.
   * @param {Array} poses - Array of detected poses.
   * @returns {MoveNetPoseWrapper|null} - Wrapped pose of patient or null.
   */
  detect_patient(poses) {
    if (this.patient_id !== -1) {
      for (let person of poses) {
        if (person.score > 0.25 && person.id === this.patient_id) {
          this.patient_ID_changed = false;
          return new MoveNetPoseWrapper(person);
        }
      }
    }
    if (!poses || poses.length === 0) return null;

    let patient_ID = null;
    let therapist_pose = null;
    let min_feet_to_center_distance = Infinity;

    for (let person of poses) {
      if (person.id === this.therapist_id) therapist_pose = person;
      if (person.score > 0.25 && person.id !== this.therapist_id) {
        let left_ankle = this.camera.scale_to_fit({
          x: person.keypoints[15].x,
          y: person.keypoints[15].y,
        });
        let right_ankle = this.camera.scale_to_fit({
          x: person.keypoints[16].x,
          y: person.keypoints[16].y,
        });
        console.log("looping in the poses");
        if (
          right_ankle.x > this.sit_stand_processing.patient_box.x &&
          left_ankle.y > this.sit_stand_processing.patient_box.y &&
          left_ankle.x <
            this.sit_stand_processing.patient_box.x +
              this.sit_stand_processing.patient_box.width &&
          right_ankle.y <
            this.sit_stand_processing.patient_box.y +
              this.sit_stand_processing.patient_box.height
        ) {
          console.log("person within the box found");

          let current_feet_to_center_distance = Math.abs(
            (left_ankle.x + right_ankle.x) / 2 -
              (this.sit_stand_processing.patient_box.x +
                (this.sit_stand_processing.patient_box.x +
                  this.sit_stand_processing.patient_box.width)) /
                2
          );
          if (current_feet_to_center_distance < min_feet_to_center_distance) {
            console.log("person ID:", person.id);
            min_feet_to_center_distance = current_feet_to_center_distance;
            patient_ID = person;
            this.patient_id = person.id;
          }
        }
      }
    }
    if (patient_ID) {
      this.patient_ID_changed = true;
      return new MoveNetPoseWrapper(patient_ID);
    }
    if (therapist_pose) {
      let left_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[15].x,
        y: therapist_pose.keypoints[15].y,
      });
      let right_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[16].x,
        y: therapist_pose.keypoints[16].y,
      });
      if (
        right_ankle.x > this.sit_stand_processing.patient_box.x &&
        left_ankle.y > this.sit_stand_processing.patient_box.y &&
        left_ankle.x <
          this.sit_stand_processing.patient_box.x +
            this.sit_stand_processing.patient_box.width &&
        right_ankle.y <
          this.sit_stand_processing.patient_box.y +
            this.sit_stand_processing.patient_box.height
      ) {
        this.patient_ID_changed = true;
        this.patient_id = therapist_pose.id;
        return new MoveNetPoseWrapper(therapist_pose);
      }
    }
    return null;
  }

  /**
   * After detecting a start gesture, switch to single-pose estimation (e.g., BlazePose)
   * to isolate the main participant. This function also chooses which bounding box
   * to pass to the single-pose model for the best detection.
   * @param {Array} poses - The array of multi-pose keypoints from the current frame.
   */
  switch_to_blazepose(poses) {
    // The therapist's selection wins over the automatic pick (it already passed the start-box check)
    const selected = this.patient_selection.selected_patient();
    let patient = selected ? new MoveNetPoseWrapper(selected) : this.detect_patient(poses);
    if (!patient) {
      console.warn("Patient not found for single-pose switch");
    } else {
      let seed_rect = this.get_blazepose_bbox(patient);
      this.switch_to_single_pose_callback(seed_rect);
      this.append_sentry_message(
        "Switching to blaze pose with seed rect: " + JSON.stringify(seed_rect)
      );
    }
  }

  /**
   * Computes the seed bounding box for BlazePose based on patient's hips and spine.
   * @param {MoveNetPoseWrapper} patient - Wrapped patient pose.
   * @returns {Object} - Seed rectangle normalized by video dimensions.
   */
  get_blazepose_bbox(patient) {
    let left_hip = patient.pose.keypoints[11];
    let right_hip = patient.pose.keypoints[12];
    let spine_base = {
      x: (left_hip.x + right_hip.x) / 2,
      y: (left_hip.y + right_hip.y) / 2,
    };
    let bbox = patient.pose.box;
    let box_size = 2 * Math.ceil(bbox.yMax * this.options.sh - spine_base.y);
    this.append_sentry_message(
      "Seed box: " +
        JSON.stringify(bbox) +
        " spine base: " +
        JSON.stringify(spine_base) +
        " box size: " +
        box_size
    );
    return {
      xCenter: spine_base.x / this.camera.video.videoWidth,
      yCenter: spine_base.y / this.camera.video.videoHeight,
      width: box_size / this.camera.video.videoWidth,
      height: box_size / this.camera.video.videoHeight,
      rotation: 0.0,
    };
  }

  /**
   * True while the patient can be changed with the remote: before each movement, from the patient
   * box check (or the movement's first instruction) until the start is accepted.
   * @returns {boolean}
   */
  is_patient_selection_open() {
    const states = UpperBodyAromAssessmentStates;
    return (
      ([
        states.begin_assessment,
        states.calibration_1,
        states.hand_orientation,
        states.abduction_adduction_calibration_2,
        states.abduction_adduction_calibration_3,
      ].includes(this.current_state) ||
        // handFlag is false once the start was accepted
        (this.current_state === states.abduction_adduction_calibration_4 && this.handFlag)) &&
      !this.voice_command_received &&
      !ASSESSMENT_STATUSES.exit_assessment_modal &&
      !ASSESSMENT_STATUSES.calibration_error_state &&
      !ASSESSMENT_STATUSES.assessment_error
    );
  }

  /**
   * Patient selection from outside the frame loop (BLE remote UP/DOWN): moves the patient to the
   * next detected person on screen. They still have to be in the patient box for the movement to start.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the selection changed.
   */
  cyclePatient(direction, source) {
    if (!this.is_patient_selection_open()) return false;

    const selected = this.patient_selection.cycle(direction);
    if (!selected) return false;

    this.patient_id = selected.id;
    this.append_sentry_message(`Patient selected by ${source}: pose id ${selected.id}`);
    return true;
  }

  /**
   * Start trigger from outside the frame loop (BLE remote START_STOP). Accepted in calibration
   * phase 4 with the selected participant in the box, also while the prompt is still playing (it
   * is cut short). Starts the current movement.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStartTrigger(source) {
    // A remote start needs a participant selected with UP/DOWN (a hand raise or voice start doesn't)
    const patient = this.patient_selection.selected_patient();
    if (
      !patient ||
      this.current_state !==
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 ||
      !this.handFlag ||
      this.voice_command_received ||
      !this.are_within_bbox([patient], this.sit_stand_processing.patient_box)
    ) {
      return false;
    }
    // Same flag as voice START, so update() starts the movement through the existing path
    this.voice_command_received = true;
    // Cut the prompt short: the remote may start while it is still playing
    this.audio_player.stop();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.append_sentry_message(`Start triggered by ${source}`);
    return true;
  }

  /**
   * Stop trigger from outside the frame loop (BLE remote START_STOP). Accepted only while a
   * movement is running (start triggered, movement not yet finished or errored). Freezes update()
   * so nothing more is measured while the caller shows the exit choice.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStopTrigger(source) {
    const running =
      // handFlag is false between the start and pre_assessment (set after a 5 s timeout)
      (this.current_state ===
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 &&
        !this.handFlag) ||
      this.current_state === UpperBodyAromAssessmentStates.pre_assessment ||
      this.current_state ===
        UpperBodyAromAssessmentStates.abduction_adduction_assessment;
    if (
      !running ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment stopped by ${source}`);
    return true;
  }

  /**
   * NEXT trigger from outside the frame loop (BLE remote NEXT). Accepted only while no movement is
   * running:
   *   - during the setup calibration (begin_assessment): leave the assessment (caller exits without a modal)
   *   - during a movement's setup or raise-hand step, not the last movement: skip it and move to the next one
   *   - last movement, none measured yet: leave the assessment (caller exits without a modal)
   *   - last movement, some measured: skip it and complete, which shows the results
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {"next_phase"|"complete"|"exit"|null} What happened, or null if NEXT doesn't apply now.
   */
  receiveNextTrigger(source) {
    const calibrating =
      this.current_state === UpperBodyAromAssessmentStates.begin_assessment;
    const between_movements =
      [
        UpperBodyAromAssessmentStates.calibration_1,
        UpperBodyAromAssessmentStates.hand_orientation,
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_2,
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_3,
      ].includes(this.current_state) ||
      // handFlag is false once the start was accepted (then it's running)
      (this.current_state ===
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 &&
        this.handFlag);
    if (
      !(calibrating || between_movements) ||
      this.voice_command_received ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return null;
    }

    if (calibrating) {
      ASSESSMENT_STATUSES.exit_assessment_modal = true;
      this.append_sentry_message(`Assessment exited by ${source} (NEXT during calibration)`);
      return "exit";
    }

    const is_last_movement =
      this.current_arom_sub_assessment ===
      AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation;
    if (is_last_movement && this.completed_phase_count === 0) {
      ASSESSMENT_STATUSES.exit_assessment_modal = true;
      this.append_sentry_message(`Assessment exited by ${source} (NEXT, no movement measured)`);
      return "exit";
    }

    this.skip_current_phase(source);
    if (is_last_movement) {
      this.current_state = UpperBodyAromAssessmentStates.all_sub_assessments_complete;
      return "complete";
    }
    this.switch_to_next_sub_assessment_state();
    return "next_phase";
  }

  // Leaves the current movement without measuring it and records it as skipped
  skip_current_phase(source) {
    this.skipped_phases.push(this.current_arom_sub_assessment);
    this.rewind_target = this.current_arom_sub_assessment;
    // Close this phase's prompts before the transition opens the next phase's own
    document.getElementById("hdnClosePhasePrompts")?.click();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.audio_player.stop();
    this.display_modalFaceCamera = false;
    this.append_sentry_message(
      `Phase ${this.current_arom_sub_assessment} skipped by ${source}`
    );
  }

  /**
   * REWIND trigger from outside the frame loop (BLE remote REWIND). Goes back exactly one movement:
   * redoes the movement that most recently completed or was skipped with NEXT. Active from then until
   * the next movement's start is accepted, and on the assessment-complete modal after the last movement
   * (the caller closes the modal). After a rewind it stays inactive until a movement completes or is
   * skipped again, so it never goes back more than one movement.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @param {boolean} complete_modal_open - Whether the assessment-complete modal is showing.
   * @returns {boolean} True if it rewound.
   */
  receiveRewindTrigger(source, complete_modal_open = false) {
    if (
      this.rewind_target === null ||
      this.voice_command_received ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state ||
      ASSESSMENT_STATUSES.assessment_error
    ) {
      return false;
    }
    const from_complete =
      this.current_state === UpperBodyAromAssessmentStates.test_complete;
    const between_movements =
      [
        UpperBodyAromAssessmentStates.post_abduction_adduction_modal,
        UpperBodyAromAssessmentStates.calibration_1,
        UpperBodyAromAssessmentStates.hand_orientation,
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_2,
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_3,
      ].includes(this.current_state) ||
      // handFlag is false once the start was accepted (then it's running)
      (this.current_state ===
        UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 &&
        this.handFlag);
    if (from_complete ? !complete_modal_open : !between_movements) return false;

    this.rewind_to_phase(this.rewind_target, source);
    return true;
  }

  // Makes `phase` (the one that completed or was skipped) the current movement again and restarts it
  rewind_to_phase(phase, source) {
    if (this.skipped_phases.includes(phase)) {
      this.skipped_phases = this.skipped_phases.filter((p) => p !== phase);
    } else {
      this.completed_phase_count--;
    }
    this.rewind_target = null;

    this.restart_phase(phase, source);
  }

  /**
   * REWIND on the "Incomplete Test" choice after a stop (BLE remote): restarts the movement that was
   * stopped, from its first instruction, keeping the earlier movements' results. That movement was neither completed nor
   * skipped, so the counts don't change.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if it restarted (only while a stop has the assessment frozen).
   */
  rewindStoppedPhase(source) {
    if (
      !ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.assessment_error ||
      this.current_state === UpperBodyAromAssessmentStates.test_complete
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = false; // unfreeze update()
    this.restart_phase(this.current_arom_sub_assessment, `${source} after a stop`);
    return true;
  }

  /**
   * REWIND on the Assessment Error modal (BLE remote): restarts the movement that errored, from its first
   * instruction, keeping the earlier movements' results, like the modal's Retry. Only for an error inside a
   * movement (assessment_error is set); a setup failure such as the depth API leaves it unset, and the
   * caller restarts the whole assessment instead.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if it restarted.
   */
  rewindErroredPhase(source) {
    if (!ASSESSMENT_STATUSES.assessment_error) return false;
    this.restart_phase(this.current_arom_sub_assessment, `${source} after an error`);
    return true;
  }

  // Makes `phase` the current movement and restarts it from its first instruction
  restart_phase(phase, source) {
    document.getElementById("hdnClosePhasePrompts")?.click();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.audio_player.stop();
    this.display_modalFaceCamera = false;

    const recorders = {
      [AROM_ASSESSMENT.Shoulder_AAA]: [recordAdductionAbduction, "1"],
      [AROM_ASSESSMENT.Right_Shoulder_FAE]: [recordRightShoulderFlexExt, "2"],
      [AROM_ASSESSMENT.Right_Elbow_FAE]: [recordRightElbowFlexionExtension, "2"],
      [AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation]: [recordRightInternalExternalShoulderRotation, "2"],
      [AROM_ASSESSMENT.Left_Shoulder_FAE]: [recordLeftShoulderFlexExt, "3"],
      [AROM_ASSESSMENT.Left_Elbow_FAE]: [recordLeftElbowFlexionExtension, "3"],
      [AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation]: [recordLeftInternalExternalShoulderRotation, "3"],
    };
    const [record, phase_no] = recorders[phase];
    this.current_arom_sub_assessment = phase;
    this.assessment_function = (poses) => record(this, poses);
    sessionStorage.setItem("phaseNo", phase_no);
    sessionStorage.setItem("aromAssessmentIndex", phase);

    // Same reset as the error modal's Retry (back to MoveNet, this movement's result cleared), but from the
    // movement's first instruction instead of calibration_2, as when the movement starts normally: the first
    // movement of each group starts at calibration_1, the others at hand_orientation
    this.retryCurrentPhase();
    this.current_state = [
      AROM_ASSESSMENT.Shoulder_AAA,
      AROM_ASSESSMENT.Right_Shoulder_FAE,
      AROM_ASSESSMENT.Left_Shoulder_FAE,
    ].includes(phase)
      ? UpperBodyAromAssessmentStates.calibration_1
      : UpperBodyAromAssessmentStates.hand_orientation;
    this.append_sentry_message(`Rewound to ${phase} by ${source}`);
  }

  /**
   * Appends timestamped messages to the Sentry log stored in ASSESSMENT_STATUSES.
   * @param {string} message - Message to log.
   */
  append_sentry_message(message) {
    let event = new Date();
    ASSESSMENT_STATUSES.sentry_message += `\n${event.toISOString()}: ${message}`;
    if (message === "Raise your hand to Calibrate.") {
      this.sentry_start_time = Date.now();
      setTimeout(() => {
        if (
          this.current_state === UpperBodyAromAssessmentStates.with_in_bounds
        ) {
          ASSESSMENT_STATUSES.sentry_message +=
            "\nGesture (Raise Hand) not detected for 30 Seconds";
        }
      }, 30000);
    } else if (message === "Calibration Completed.") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message += `,  Raise Hand Calibration time: ${diff} seconds`;
    }
  }

  /**
   * Advances to the next sub-assessment, resets UI and state,
   * and selects the appropriate next recording function.
   */
  switch_to_next_sub_assessment_state() {
    this.arom_right_high_value.innerText = "0°";
    this.arom_right_low_value.innerText = "0°";
    this.arom_left_high_value.innerText = "0°";
    this.arom_left_low_value.innerText = "0°";
    this.arom_assessment_counter_left.innerText = "0°";
    this.arom_assessment_counter_right.innerText = "0°";
    this.reset_pose_estimator();
    this.switch_to_multi_pose_callback();
    this.gesture_detector.reset();

    switch (this.current_arom_sub_assessment) {
      case AROM_ASSESSMENT.Shoulder_AAA:
        this.current_arom_sub_assessment = AROM_ASSESSMENT.Right_Shoulder_FAE;
        this.assessment_function = (poses) =>
          recordRightShoulderFlexExt(this, poses);
        sessionStorage.setItem("phaseNo", "2");
        sessionStorage.setItem(
          "aromAssessmentIndex",
          AROM_ASSESSMENT.Right_Shoulder_FAE
        );
        this.current_state = UpperBodyAromAssessmentStates.calibration_1;
        document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
        this.first_frame_time = this.current_time;
        this.patient_within_box_check = false;
        this.patient_within_box_timer = 0;
        this.within_bound_start_flag = false;
        this.assessment_timer = 0;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.adduction_abduction.left_abduction = 0;
          calculation_metric.adduction_abduction.left_adduction = 0;
          calculation_metric.adduction_abduction.right_abduction = 0;
          calculation_metric.adduction_abduction.right_adduction = 0;
        }
        break;

      case AROM_ASSESSMENT.Left_Shoulder_FAE:
        this.current_arom_sub_assessment = AROM_ASSESSMENT.Left_Elbow_FAE;
        this.assessment_function = (poses) =>
          recordLeftElbowFlexionExtension(this, poses);
        sessionStorage.setItem("phaseNo", "3");
        sessionStorage.setItem(
          "aromAssessmentIndex",
          AROM_ASSESSMENT.Left_Elbow_FAE
        );
        this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.shoulder_flexion_extension.right_extension = 0;
          calculation_metric.shoulder_flexion_extension.right_flexion = 0;
        }
        break;

      case AROM_ASSESSMENT.Left_Elbow_FAE:
        this.current_arom_sub_assessment =
          AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation;
        this.assessment_function = (poses) =>
          recordLeftInternalExternalShoulderRotation(this, poses);
        sessionStorage.setItem("phaseNo", "3");
        sessionStorage.setItem(
          "aromAssessmentIndex",
          AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation
        );
        this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.elbow_flexion_extension.right_flexion = 0;
          calculation_metric.elbow_flexion_extension.right_extension = 0;
        }
        break;

      case AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation:
        this.current_state =
          UpperBodyAromAssessmentStates.all_sub_assessments_complete;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.internal_external_shoulder_rotation.right_internal = 0;
          calculation_metric.internal_external_shoulder_rotation.right_external = 0;
        }
        break;

      case AROM_ASSESSMENT.Right_Shoulder_FAE:
        this.current_arom_sub_assessment = AROM_ASSESSMENT.Right_Elbow_FAE;
        this.assessment_function = (poses) =>
          recordRightElbowFlexionExtension(this, poses);
        sessionStorage.setItem("phaseNo", "2");
        sessionStorage.setItem(
          "aromAssessmentIndex",
          AROM_ASSESSMENT.Right_Elbow_FAE
        );
        this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.shoulder_flexion_extension.left_extension = 0;
          calculation_metric.shoulder_flexion_extension.left_flexion = 0;
        }
        break;

      case AROM_ASSESSMENT.Right_Elbow_FAE:
        this.current_arom_sub_assessment =
          AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation;
        this.assessment_function = (poses) =>
          recordRightInternalExternalShoulderRotation(this, poses);
        sessionStorage.setItem("phaseNo", "2");
        sessionStorage.setItem(
          "aromAssessmentIndex",
          AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation
        );
        this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.elbow_flexion_extension.left_flexion = 0;
          calculation_metric.elbow_flexion_extension.left_extension = 0;
        }
        break;

      case AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation:
        this.current_arom_sub_assessment = AROM_ASSESSMENT.Left_Shoulder_FAE;
        this.assessment_function = (poses) =>
          recordLeftShoulderFlexExt(this, poses);
        sessionStorage.setItem("phaseNo", "3");
        sessionStorage.setItem(
          "aromAssessmentIndex",
          AROM_ASSESSMENT.Left_Shoulder_FAE
        );
        this.current_state = UpperBodyAromAssessmentStates.calibration_1;
        document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
        this.first_frame_time = this.current_time;
        this.patient_within_box_check = false;
        this.patient_within_box_timer = 0;
        this.within_bound_start_flag = false;
        this.assessment_timer = 0;

        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
          calculation_metric.internal_external_shoulder_rotation.left_internal = 0;
          calculation_metric.internal_external_shoulder_rotation.left_external = 0;
        }
        break;

      default:
        break;
    }
  }

  /**
   * Checks for patient leaving or occlusion errors mid-assessment.
   * If errors exceed thresholds, aborts test and logs error.
   * @param {Array} poses - Array of detected poses.
   */
  assessment_error_check(poses) {
    let pose = this.detect_patient(poses);
    if (!poses || poses.length === 0) {
      this.frames_with_no_patient++;
    } else {
      this.frames_with_no_patient = 0;
      if (pose) {
        let left = this.camera.scale_to_fit({
          x: pose.leftAnkle.x,
          y: pose.leftAnkle.y,
        });
        let right = this.camera.scale_to_fit({
          x: pose.rightAnkle.x,
          y: pose.rightAnkle.y,
        });
        if (
          right.x > this.sit_stand_processing.extended_patient_box.x &&
          left.y > this.sit_stand_processing.extended_patient_box.y &&
          left.x <
            this.sit_stand_processing.extended_patient_box.x +
              this.sit_stand_processing.extended_patient_box.width &&
          right.y <
            this.sit_stand_processing.extended_patient_box.y +
              this.sit_stand_processing.extended_patient_box.height
        ) {
          this.frames_with_patient_outside_extended_box = 0;
        } else {
          this.frames_with_patient_outside_extended_box++;
        }
      }
    }
    if (
      this.frames_with_no_patient >= 15 ||
      this.frames_with_patient_outside_extended_box >= 3
    ) {
      this.end_time = this.current_time;
      let response = this.build_response();
      this.append_sentry_message(
        "Assessment response:" + JSON.stringify(response)
      );
      if (window["AssessmentPageReference"]) {
        window["AssessmentPageReference"].CompleteAssessment(response);
      }
      // Stop voice listening on assessment error
      this.cleanupVoiceListeners();
      this.current_state = UpperBodyAromAssessmentStates.test_complete;
      ASSESSMENT_STATUSES.assessment_error = true;
      ASSESSMENT_STATUSES.assessment_Completed = true;
      localStorage.removeItem("modalDisplayed");
      this.audio_player.play(
        UpperBodyAromAssessmentStates.assessment_interrupted
      );
      this.append_sentry_message(
        "Assessment ERROR occured - Someone has crossed the Patient during assessment or patient has left the Patient region."
      );
      this.frames_with_no_patient = 0;
      this.frames_with_patient_outside_extended_box = 0;
    }
  }

  /**
   * Allows therapist to restart the current sub-assessment,
   * resetting relevant metrics and UI state.
   */
  restart_sub_assessment_feature() {
    if (AromMenuOptions.restartSubAssessment) {
      AromMenuOptions.restartSubAssessment = false;
      // Cleanup voice listeners before restarting
      this.cleanupVoiceListeners();
      // --------------------------- RESTART SUB-ASSESSMENT --------------------------------
      if (
        this.current_state === UpperBodyAromAssessmentStates.hand_orientation ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_2 ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_3 ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 ||
        this.current_state === UpperBodyAromAssessmentStates.pre_assessment ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_assessment ||
        this.current_state ===
          UpperBodyAromAssessmentStates.post_abduction_adduction_modal
      ) {
        this.reset_pose_estimator();
        this.switch_to_multi_pose_callback();
        this.gesture_detector.reset();
        // hide real‑time angle UI
        this.arom_realtime_angle_left.style.display = "none";
        this.arom_realtime_angle_right.style.display = "none";
        // stop counter if in assessment
        if (
          this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_assessment
        ) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        // reset based on which sub‑assessment we're in
        switch (this.current_arom_sub_assessment) {
          case AROM_ASSESSMENT.Shoulder_AAA:
            this.assessment_function = (poses) =>
              recordAdductionAbduction(this, poses);
            sessionStorage.setItem("phaseNo", "1");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Shoulder_AAA
            );
            this.current_state = UpperBodyAromAssessmentStates.calibration_1;
            document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
            this.first_frame_time = this.current_time;
            this.patient_within_box_check = false;
            this.patient_within_box_timer = 0;
            this.within_bound_start_flag = false;
            this.assessment_timer = 0;
            calculation_metric.adduction_abduction.left_abduction = null;
            calculation_metric.adduction_abduction.left_adduction = null;
            calculation_metric.adduction_abduction.right_abduction = null;
            calculation_metric.adduction_abduction.right_adduction = null;
            break;

          case AROM_ASSESSMENT.Left_Shoulder_FAE:
            // keep same sub‑assessment index
            this.assessment_function = (poses) =>
              recordLeftShoulderFlexExt(this, poses);
            sessionStorage.setItem("phaseNo", "3");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Left_Shoulder_FAE
            );
            this.current_state = UpperBodyAromAssessmentStates.calibration_1;
            document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
            this.first_frame_time = this.current_time;
            this.patient_within_box_check = false;
            this.patient_within_box_timer = 0;
            this.within_bound_start_flag = false;
            this.assessment_timer = 0;
            calculation_metric.shoulder_flexion_extension.right_flexion = null;
            calculation_metric.shoulder_flexion_extension.right_extension =
              null;
            break;

          case AROM_ASSESSMENT.Left_Elbow_FAE:
            this.assessment_function = (poses) =>
              recordLeftElbowFlexionExtension(this, poses);
            sessionStorage.setItem("phaseNo", "3");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Left_Elbow_FAE
            );
            this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
            calculation_metric.elbow_flexion_extension.right_flexion = null;
            calculation_metric.elbow_flexion_extension.right_extension = null;
            break;

          case AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation:
            this.assessment_function = (poses) =>
              recordLeftInternalExternalShoulderRotation(this, poses);
            sessionStorage.setItem("phaseNo", "3");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation
            );
            this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
            calculation_metric.internal_external_shoulder_rotation.right_internal =
              null;
            calculation_metric.internal_external_shoulder_rotation.right_external =
              null;
            break;

          case AROM_ASSESSMENT.Right_Shoulder_FAE:
            this.assessment_function = (poses) =>
              recordRightShoulderFlexExt(this, poses);
            sessionStorage.setItem("phaseNo", "2");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Right_Shoulder_FAE
            );
            this.current_state = UpperBodyAromAssessmentStates.calibration_1;
            document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
            this.first_frame_time = this.current_time;
            this.patient_within_box_check = false;
            this.patient_within_box_timer = 0;
            this.within_bound_start_flag = false;
            this.assessment_timer = 0;
            calculation_metric.shoulder_flexion_extension.left_flexion = null;
            calculation_metric.shoulder_flexion_extension.left_extension = null;
            break;

          case AROM_ASSESSMENT.Right_Elbow_FAE:
            this.assessment_function = (poses) =>
              recordRightElbowFlexionExtension(this, poses);
            sessionStorage.setItem("phaseNo", "2");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Right_Elbow_FAE
            );
            this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
            calculation_metric.elbow_flexion_extension.left_flexion = null;
            calculation_metric.elbow_flexion_extension.left_extension = null;
            break;

          case AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation:
            this.assessment_function = (poses) =>
              recordRightInternalExternalShoulderRotation(this, poses);
            sessionStorage.setItem("phaseNo", "2");
            sessionStorage.setItem(
              "aromAssessmentIndex",
              AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation
            );
            this.current_state = UpperBodyAromAssessmentStates.hand_orientation;
            calculation_metric.internal_external_shoulder_rotation.left_internal =
              null;
            calculation_metric.internal_external_shoulder_rotation.left_external =
              null;
            break;

          default:
            break;
        }
      }
    }
  }

  /**
   * Allows therapist to restart the entire current phase,
   * resetting relevant metrics and UI state.
   */
  restart_phase_feature() {
    if (AromMenuOptions.restartPhase) {
      AromMenuOptions.restartPhase = false;
      this.cleanupVoiceListeners();
      this.reset_pose_estimator();
      this.switch_to_multi_pose_callback();
      this.gesture_detector.reset();
      // --------------------------- RESTART PHASE --------------------------------
      if (
        this.current_state === UpperBodyAromAssessmentStates.hand_orientation ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_2 ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_3 ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 ||
        this.current_state === UpperBodyAromAssessmentStates.pre_assessment ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_assessment ||
        this.current_state ===
          UpperBodyAromAssessmentStates.post_abduction_adduction_modal
      ) {
        // hide real‑time angle UI
        this.arom_realtime_angle_left.style.display = "none";
        this.arom_realtime_angle_right.style.display = "none";
        if (
          this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_assessment
        ) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        // reset entire phase back to first sub‑assessment of current side
        if (this.current_arom_sub_assessment === AROM_ASSESSMENT.Shoulder_AAA) {
          this.assessment_function = (poses) =>
            recordAdductionAbduction(this, poses);
          sessionStorage.setItem("phaseNo", "1");
          sessionStorage.setItem(
            "aromAssessmentIndex",
            AROM_ASSESSMENT.Shoulder_AAA
          );
          this.current_state = UpperBodyAromAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
          calculation_metric.adduction_abduction.left_abduction = null;
          calculation_metric.adduction_abduction.left_adduction = null;
          calculation_metric.adduction_abduction.right_abduction = null;
          calculation_metric.adduction_abduction.right_adduction = null;
        } else if (
          [
            AROM_ASSESSMENT.Right_Shoulder_FAE,
            AROM_ASSESSMENT.Right_Elbow_FAE,
            AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation,
          ].includes(this.current_arom_sub_assessment)
        ) {
          // restart right‑side sequence
          this.current_arom_sub_assessment = AROM_ASSESSMENT.Right_Shoulder_FAE;
          this.assessment_function = (poses) =>
            recordRightShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "2");
          sessionStorage.setItem(
            "aromAssessmentIndex",
            AROM_ASSESSMENT.Right_Shoulder_FAE
          );
          this.current_state = UpperBodyAromAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
          calculation_metric.shoulder_flexion_extension.left_flexion = null;
          calculation_metric.shoulder_flexion_extension.left_extension = null;
          calculation_metric.elbow_flexion_extension.left_flexion = null;
          calculation_metric.elbow_flexion_extension.left_extension = null;
          calculation_metric.internal_external_shoulder_rotation.right_internal =
            null;
          calculation_metric.internal_external_shoulder_rotation.right_external =
            null;
        } else if (
          [
            AROM_ASSESSMENT.Left_Shoulder_FAE,
            AROM_ASSESSMENT.Left_Elbow_FAE,
            AROM_ASSESSMENT.Left_Shoulder_IAE_Rotation,
          ].includes(this.current_arom_sub_assessment)
        ) {
          // restart left‑side sequence
          this.current_arom_sub_assessment = AROM_ASSESSMENT.Left_Shoulder_FAE;
          this.assessment_function = (poses) =>
            recordLeftShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "3");
          sessionStorage.setItem(
            "aromAssessmentIndex",
            AROM_ASSESSMENT.Left_Shoulder_FAE
          );
          this.current_state = UpperBodyAromAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
          calculation_metric.shoulder_flexion_extension.right_flexion = null;
          calculation_metric.shoulder_flexion_extension.right_extension = null;
          calculation_metric.elbow_flexion_extension.right_flexion = null;
          calculation_metric.elbow_flexion_extension.right_extension = null;
          calculation_metric.internal_external_shoulder_rotation.right_internal =
            null;
          calculation_metric.internal_external_shoulder_rotation.right_external =
            null;
        }
      }
    }
  }

  /**
   * Allows therapist to skip the current phase,
   * advancing directly to the next stage.
   */
  skip_phase_feature() {
    if (AromMenuOptions.skipPhase) {
      AromMenuOptions.skipPhase = false;
      this.cleanupVoiceListeners();
      this.reset_pose_estimator();
      this.switch_to_multi_pose_callback();
      this.gesture_detector.reset();
      // --------------------------- SKIP PHASE --------------------------------
      if (
        this.current_state === UpperBodyAromAssessmentStates.hand_orientation ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_2 ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_3 ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_calibration_4 ||
        this.current_state === UpperBodyAromAssessmentStates.pre_assessment ||
        this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_assessment ||
        this.current_state ===
          UpperBodyAromAssessmentStates.post_abduction_adduction_modal
      ) {
        this.arom_realtime_angle_left.style.display = "none";
        this.arom_realtime_angle_right.style.display = "none";
        if (
          this.current_state ===
          UpperBodyAromAssessmentStates.abduction_adduction_assessment
        ) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        if (this.current_arom_sub_assessment === AROM_ASSESSMENT.Shoulder_AAA) {
          // skip straight to right‑shoulder flex/ext
          this.current_arom_sub_assessment = AROM_ASSESSMENT.Right_Shoulder_FAE;
          this.assessment_function = (poses) =>
            recordRightShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "2");
          sessionStorage.setItem(
            "aromAssessmentIndex",
            AROM_ASSESSMENT.Right_Shoulder_FAE
          );
          this.current_state = UpperBodyAromAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          [
            AROM_ASSESSMENT.Right_Shoulder_FAE,
            AROM_ASSESSMENT.Right_Elbow_FAE,
            AROM_ASSESSMENT.Right_Shoulder_IAE_Rotation,
          ].includes(this.current_arom_sub_assessment)
        ) {
          // skip to left‑shoulder flex/ext
          this.current_arom_sub_assessment = AROM_ASSESSMENT.Left_Shoulder_FAE;
          this.assessment_function = (poses) =>
            recordLeftShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "3");
          sessionStorage.setItem(
            "aromAssessmentIndex",
            AROM_ASSESSMENT.Left_Shoulder_FAE
          );
          this.current_state = UpperBodyAromAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else {
          // if already on last sub‑assessment, mark complete
          this.current_state =
            UpperBodyAromAssessmentStates.all_sub_assessments_complete;
        }
      }
    }
  }
}

export default UpperBodyArom;
